import { useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Link as B2BLink, paletteColors, Stack, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { getEnvRequired } from '../../shared/utilities/env';
import { HttpError } from '../../resources/shared/api/client';
import { useConnectCalendarMutation } from '../../resources/calendarConfig/mutations';
import { DEFAULT_VIEW_ROUTE } from '../../shared/routing/Routes';

const APP_BASENAME = getEnvRequired('APP_BASENAME');

function buildRedirectUri(): string {
    return `${window.location.origin}${APP_BASENAME}/calendar/callback`;
}

export const CalendarCallbackPage = (): JSX.Element => {
    const location = useLocation();
    const mutation = useConnectCalendarMutation();
    const hasFired = useRef(false);
    const { formatMessage } = useIntl();

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const error = params.get('error');

    useEffect(() => {
        if (code && !hasFired.current) {
            hasFired.current = true;
            mutation.mutate({
                provider: 'google',
                authorizationCode: code,
                redirectUri: buildRedirectUri(),
            });
        }
        // hasFired and mutation.mutate are stable refs — intentional omission
        // eslint-disable-next-line react-hooks/exhaustive-deps -- hasFired and mutation.mutate are stable refs, intentional omission
    }, []);

    const backToHomeLink = (
        <B2BLink
            component={RouterLink}
            to={DEFAULT_VIEW_ROUTE}
            data-spec-id="calendar-callback-back-home"
        >
            {formatMessage({ id: 'calendar.callback.back_to_home' })}
        </B2BLink>
    );

    const content = (() => {
        if (error === 'access_denied') {
            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connect-refused-message">
                        {formatMessage({ id: 'calendar.connect.refused' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        if (mutation.isPending) {
            return <Typography>{formatMessage({ id: 'calendar.connect.loading' })}</Typography>;
        }

        if (mutation.isSuccess) {
            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connected-status">
                        {formatMessage({ id: 'calendar.connected.status' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        if (mutation.isError) {
            const httpStatus =
                mutation.error instanceof HttpError ? mutation.error.status : undefined;

            if (httpStatus === 409) {
                return (
                    <Stack gap={2} alignItems="center">
                        <Typography data-spec-id="calendar-already-connected-message">
                            {formatMessage({ id: 'calendar.connect.already_connected' })}
                        </Typography>
                        <Typography data-spec-id="calendar-disconnect-prompt">
                            {formatMessage({ id: 'calendar.connect.disconnect_first' })}
                        </Typography>
                        {backToHomeLink}
                    </Stack>
                );
            }

            return (
                <Stack gap={2} alignItems="center">
                    <Typography data-spec-id="calendar-connect-error-message">
                        {formatMessage({ id: 'calendar.connect.error_message' })}
                    </Typography>
                    {backToHomeLink}
                </Stack>
            );
        }

        return (
            <Stack gap={2} alignItems="center">
                <Typography>
                    {formatMessage({ id: 'calendar.connect.invalid_callback' })}
                </Typography>
                {backToHomeLink}
            </Stack>
        );
    })();

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: paletteColors.neutral[0],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                textAlign: 'center',
            }}
        >
            {content}
        </Box>
    );
};
